import React from 'react';
import { ChevronLeft } from 'react-iconly';
import { COMPONENTS } from '../../../lib/styleGuide';

/**
 * BackButton Component
 * 
 * A standardized back button that follows the design system guidelines.
 * 
 * @param {Object} props
 * @param {Function} props.onClick - Function to call when button is clicked
 * @param {string} props.variant - Button variant: 'standard', 'small', 'link'
 * @param {string} props.size - Icon size override: 'small', 'medium', 'large'
 * @param {string} props.color - Icon color override
 * @param {string} props.className - Additional CSS classes
 * @param {Object} props.style - Additional inline styles
 * @param {string} props.ariaLabel - Accessibility label (defaults to "Go back")
 */
export function BackButton({ 
  onClick, 
  variant = 'standard', 
  size, 
  color, 
  className = '', 
  style = {},
  ariaLabel = "Go back",
  ...props 
}) {
  // Determine icon size based on variant and size prop
  const getIconSize = () => {
    if (size) return COMPONENTS.BACK_BUTTON.ICON.SIZE[size.toUpperCase()] || COMPONENTS.BACK_BUTTON.ICON.SIZE.MEDIUM;
    
    switch (variant) {
      case 'small':
        return COMPONENTS.BACK_BUTTON.ICON.SIZE.SMALL;
      case 'link':
        return COMPONENTS.BACK_BUTTON.ICON.SIZE.SMALL;
      case 'standard':
      default:
        return COMPONENTS.BACK_BUTTON.ICON.SIZE.MEDIUM;
    }
  };

  // Determine icon color based on variant and color prop
  const getIconColor = () => {
    if (color) return color;
    
    switch (variant) {
      case 'link':
        return COMPONENTS.BACK_BUTTON.ICON.COLOR.LINK;
      case 'small':
        return COMPONENTS.BACK_BUTTON.ICON.COLOR.SECONDARY;
      case 'standard':
      default:
        return COMPONENTS.BACK_BUTTON.ICON.COLOR.PRIMARY;
    }
  };

  // Get button styles based on variant
  const getButtonStyles = () => {
    const baseStyles = {
      background: COMPONENTS.BACK_BUTTON.BUTTON.BACKGROUND,
      border: COMPONENTS.BACK_BUTTON.BUTTON.BORDER,
      borderRadius: COMPONENTS.BACK_BUTTON.BUTTON.BORDER_RADIUS,
      cursor: COMPONENTS.BACK_BUTTON.BUTTON.CURSOR,
      transition: COMPONENTS.BACK_BUTTON.BUTTON.TRANSITION,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    };

    switch (variant) {
      case 'small':
        return {
          ...baseStyles,
          padding: COMPONENTS.BACK_BUTTON.BUTTON.PADDING.SMALL
        };
      case 'link':
        return {
          ...baseStyles,
          padding: COMPONENTS.BACK_BUTTON.BUTTON.PADDING.SMALL,
          background: 'transparent'
        };
      case 'standard':
      default:
        return {
          ...baseStyles,
          padding: COMPONENTS.BACK_BUTTON.BUTTON.PADDING.MEDIUM
        };
    }
  };

  const buttonStyles = {
    ...getButtonStyles(),
    ...style
  };

  return (
    <button
      onClick={onClick}
      style={buttonStyles}
      className={className}
      aria-label={ariaLabel}
      {...props}
    >
      <ChevronLeft 
        set="regular" 
        size={getIconSize()} 
        primaryColor={getIconColor()} 
      />
    </button>
  );
}

export default BackButton;
